/**
 * Push notifications on the phone (Phase 6 part 2). The server decides who
 * gets what (migration 0036) and Firebase delivers it; the app only:
 *   • asks permission once after sign-in (Android 13+ shows the system prompt),
 *   • hands the phone's token to the server on every launch (register_push_token),
 *   • opens the right screen when a notification is tapped (data.route),
 *   • shows a toast when one arrives while the app is open,
 *   • forgets the phone on sign-out (unregister_push_token).
 *
 * Android only for now: iPhone push needs the APNs key from the Apple
 * developer account, and iPhones will register once that's set up.
 * Nothing here runs on the website.
 */
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { kvGet, kvSet } from '@routeback/core';
import { supabase } from './supabase';

export type PushState = 'unsupported' | 'prompt' | 'granted' | 'denied';

/** Phones that can get push today. */
export function pushSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

type Plugin = typeof import('@capacitor/push-notifications').PushNotifications;
type PluginModule = typeof import('@capacitor/push-notifications');
let mod: Promise<PluginModule> | null = null;
/**
 * The plugin's module, loaded on first use. Read `.PushNotifications` off it
 * where it's used — never resolve a promise WITH the plugin (no
 * `async () => PushNotifications`, no `.then((m) => m.PushNotifications)`): a
 * Capacitor plugin is a proxy that answers every property, `then` included,
 * so the promise mistakes it for a thenable and waits forever on a native
 * "then" call that doesn't exist.
 */
const loadMod = (): Promise<PluginModule> => (mod ??= import('@capacitor/push-notifications'));

let companyId: string | null = null;
let token: string | null = null;
let handles: PluginListenerHandle[] = [];
let onArrive: ((title: string, body: string, route: string) => void) | null = null;
let onSwitch: ((company: string) => Promise<void>) | null = null;

/** Where a tapped notification goes (hash routes). Only in-app paths. */
export function routeFrom(data: unknown): string {
  const r = String((data as { route?: unknown } | null)?.route ?? '');
  return /^\/[A-Za-z0-9/_-]*$/.test(r) ? r : '/home';
}

async function sendToken(): Promise<void> {
  if (!supabase || !companyId || !token) return;
  const { error } = await supabase.rpc('register_push_token', { c: companyId, tok: token, plat: Capacitor.getPlatform() });
  if (error) console.warn('[push] not registered', error.message);
}

async function listen(p: Plugin): Promise<void> {
  if (handles.length) return;
  handles = await Promise.all([
    p.addListener('registration', (t) => { token = t.value; void sendToken(); }),
    p.addListener('registrationError', (e) => console.warn('[push] registration failed', e.error)),
    p.addListener('pushNotificationReceived', (n) => onArrive?.(n.title ?? '', n.body ?? '', routeFrom(n.data))),
    p.addListener('pushNotificationActionPerformed', (a) => openTapped(a.notification.data)),
  ]);
}

/**
 * A tapped notification opens its screen. A mechanic works for several
 * companies: a job at another company switches to it first (like the shop view).
 */
export function openTapped(data: unknown, current = companyId, switchTo = onSwitch): void {
  window.location.hash = `#${routeFrom(data)}`;
  const c = String((data as { company_id?: unknown } | null)?.company_id ?? '');
  if (c && current && c !== current && switchTo) void switchTo(c).catch(() => undefined);
}

/** Current permission, without asking. */
export async function pushPermission(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const s = (await (await loadMod()).PushNotifications.checkPermissions()).receive;
  return s === 'granted' ? 'granted' : s === 'denied' ? 'denied' : 'prompt';
}

async function register(p: Plugin): Promise<void> {
  // Android 8+: notifications appear on a channel; "routeback" matches the server's messages.
  await p.createChannel({ id: 'routeback', name: 'RouteBack', description: 'Time off, truck problems, uniforms, documents, messages from your managers and shop jobs', importance: 4, visibility: 1 }).catch(() => undefined);
  await p.register();
}

/** Ask (if not asked yet) and register. Returns the resulting state. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const p = (await loadMod()).PushNotifications;
  await listen(p);
  let s = await pushPermission();
  if (s === 'prompt') s = (await p.requestPermissions()).receive === 'granted' ? 'granted' : 'denied';
  if (s === 'granted') await register(p);
  return s;
}

/**
 * Called once the person is signed in to a company: listen, ask the first time
 * (never again — after that it's Me → Notifications), refresh the token.
 */
export async function startPush(company: string, arrive: (title: string, body: string, route: string) => void, switchTo?: (company: string) => Promise<void>): Promise<void> {
  if (!pushSupported()) return;
  companyId = company;
  onArrive = arrive;
  onSwitch = switchTo ?? null;
  const p = (await loadMod()).PushNotifications;
  await listen(p);
  const s = await pushPermission();
  if (s === 'granted') { await register(p); return; }
  if (s === 'prompt' && !(await kvGet<boolean>('push-asked'))) {
    await kvSet('push-asked', true);
    await enablePush();
  }
}

/** Queue a test notification to this person's phones (Me → Notifications). */
export async function sendTestPush(company: string): Promise<string | null> {
  if (!supabase) return 'Push needs the cloud.';
  const { error } = await supabase.rpc('push_test', { c: company });
  if (error) return error.message;
  void supabase.functions.invoke('email-worker', { body: {} }).catch(() => undefined);
  return null;
}

/** Sign-out: this phone stops getting this person's notifications. */
export async function forgetThisPhone(): Promise<void> {
  if (!pushSupported() || !supabase) return;
  try {
    if (token) await supabase.rpc('unregister_push_token', { tok: token });
    await (await loadMod()).PushNotifications.unregister();
  } catch {
    /* signing out anyway */
  }
  token = null;
  companyId = null;
}
